import { supabase } from '../../shared/supabase/supabaseClient';

export type ConsultationRow = {
  id: string;
  organization_id: string;
  branch_id: string;
  customer_user_id: string;
  customer_name: string;
  customer_phone: string | null;
  customer_dni: string | null;
  subject: string;
  body: string;
  prescription_image_url: string | null;
  status: string;
  created_at: string;
  answered_at: string | null;
  closed_at: string | null;
  read_by_customer_at: string | null;
  read_by_store_at: string | null;
  branch_name: string | null;
};

export type ConsultationMessage = {
  id: string;
  author: string;
  body: string;
  created_at: string;
};

export type ConsultationFilter = 'all' | 'unread' | 'pending' | 'answered' | 'closed';

const CONSULTATION_LIST_LIMIT = 100;

export async function listConsultationsForOrg(
  orgId: string,
  filter: ConsultationFilter = 'all',
  page?: { limit: number; offset: number },
): Promise<ConsultationRow[]> {
  const limit = Math.min(page?.limit ?? 20, CONSULTATION_LIST_LIMIT);
  const offset = page?.offset ?? 0;

  const { data, error } = await supabase.rpc('list_consultations_for_org', {
    p_org_id: orgId,
    p_limit: limit,
    p_offset: offset,
    p_filter: filter,
  });

  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ConsultationRow[];
}

export async function countUnreadConsultationsForOrg(orgId: string): Promise<number> {
  const { data, error } = await supabase.rpc('count_unread_consultations_for_org', {
    p_org_id: orgId,
  });

  if (error) throw new Error(error.message);
  const value = typeof data === 'number' ? data : Number(data ?? 0);
  return Number.isFinite(value) ? value : 0;
}

export async function getConsultationById(consultationId: string): Promise<ConsultationRow | null> {
  const { data, error } = await supabase
    .from('store_consultations')
    .select(
      'id, organization_id, branch_id, customer_user_id, customer_name, customer_phone, customer_dni, subject, body, prescription_image_url, status, created_at, answered_at, closed_at, read_by_customer_at, read_by_store_at',
    )
    .eq('id', consultationId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  // branch_name no viene en esta query directa (sin join con branches).
  // La lista (list_consultations_for_org) sí lo trae. Default null para
  // que el tipo ConsultationRow sea consistente.
  return (data as unknown as ConsultationRow) ? { ...(data as unknown as ConsultationRow), branch_name: (data as unknown as ConsultationRow).branch_name ?? null } : null;
}

export async function listConsultationMessages(consultationId: string): Promise<ConsultationMessage[]> {
  const { data, error } = await supabase
    .from('store_consultation_messages')
    .select('id, author, body, created_at')
    .eq('consultation_id', consultationId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ConsultationMessage[];
}

export async function addConsultationReply(consultationId: string, body: string): Promise<void> {
  const { error } = await supabase.rpc('add_consultation_message', {
    p_consultation_id: consultationId,
    p_author: 'store',
    p_body: body,
  });

  if (error) throw new Error(error.message);

  // #758: supabase.functions.invoke stringifica el body automáticamente.
  // Antes se pasaba JSON.stringify(...) → el Edge Function recibía un string doble.
  void supabase.functions.invoke('notify-consultation-reply', {
    body: { consultation_id: consultationId },
  }).then(
    () => {},
    (e) => console.warn('notify-consultation-reply failed', e),
  );
}

export async function markConsultationReadByStore(consultationId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_consultation_read_by_store', {
    p_consultation_id: consultationId,
  });

  if (error) throw new Error(error.message);
}

export async function closeConsultation(consultationId: string): Promise<void> {
  // BUG-15: use server-side RPC for timestamp + org validation
  const { error } = await supabase.rpc('close_consultation', {
    p_consultation_id: consultationId,
  });

  if (error) throw new Error(error.message);
}

export async function getPrescriptionSignedUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('prescription-images')
    .createSignedUrl(path, 3600);

  if (error) return null;
  return data?.signedUrl ?? null;
}
